import { Building2, Eye, Info, Plus, SaudiRiyal, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Input,
  Skeleton,
} from '@cold-ambulance/shadcn';
import {
  FormMessage,
  PageContainer,
  TheButton,
  ViewGeneratedFile,
  client,
  errorToFormIssue,
  formatDate,
  useData,
} from '@cold-ambulance/ui';

export default function AcceptOfferPage() {
  const { t } = useTranslation();
  const { requestId, offerId } = useParams();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<ReturnType<
    typeof errorToFormIssue
  > | null>(null);

  const requestQuery = useData(
    'GET /requests/{id}',
    { id: requestId! },
    { enabled: !!requestId },
  );

  const request = requestQuery.data;
  const offer = request?.offers.find((o) => o.id === offerId);

  const handleAcceptOffer = async () => {
    if (!offerId) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await client.request('POST /offers/{id}/accept', {
        id: offerId,
        requiredDocuments: documents,
      });
      navigate('/requests/offer-accepted', {
        state: {
          documentSubmissionRequired:
            'documentSubmissionRequired' in result
              ? result.documentSubmissionRequired
              : true,
          requestId: result.requestId,
          offerId: result.id,
        },
      });
    } catch (err) {
      setError(errorToFormIssue(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (requestQuery.isLoading) {
    return (
      <div className="page-container">
        <div className="mx-auto w-full max-w-2xl">
          <AcceptOfferSkeleton />
        </div>
      </div>
    );
  }

  if (!request || !offer) {
    return (
      <div className="page-container">
        <div className="mx-auto w-full max-w-2xl">
          <div className="text-center">
            <p className="text-lg">{t('acceptOffer.offerNotFound')}</p>
            <Link to="/requests" className="text-primary mt-4 inline-block">
              {t('acceptOffer.backToRequests')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const hasBankGuarantee =
    request.bankGuaranteePercent && request.bankGuaranteePercent > 0;

  return (
    <PageContainer title={t('acceptOffer.reviewOffer')} showBackButton>
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="mb-4">
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <Eye className="h-5 w-5" />
              {t('acceptOffer.financialOffer')}
            </h2>
            <p className="text-muted-foreground mt-1 text-sm">
              {t('acceptOffer.previewOfferDetails')}
            </p>
          </div>
          <ViewGeneratedFile
            endpoint="GET /offers/{id}/pdf"
            input={{ id: offer.id }}
            icon={<Eye className="h-4 w-4" />}
            variant="outline"
            className="w-full"
            label={t('acceptOffer.financialOffer')}
          />
        </CardContent>
      </Card>

      <OfferSummaryCard offer={offer} />

      <RequiredDocumentsCard
        documents={documents}
        onDocumentsChange={setDocuments}
        bankGuaranteePercent={
          hasBankGuarantee ? request.bankGuaranteePercent : null
        }
      />

      {error && <FormMessage errors={[error]} />}

      <TheButton
        onClick={handleAcceptOffer}
        loading={isSubmitting}
        className="h-12 w-full text-base"
      >
        {t('common.submit')}
      </TheButton>
    </PageContainer>
  );
}

function OfferSummaryCard({
  offer,
}: {
  offer: { price: number; notes: string | null; createdAt: string };
}) {
  const { t } = useTranslation();
  return (
    <Card className="mb-6">
      <CardContent className="p-6">
        <h2 className="mb-4 text-lg font-semibold">
          {t('acceptOffer.offerSummary')}
        </h2>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#337540]">
              <Building2 className="h-4 w-4 text-white" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">
                {t('acceptOffer.vendor')}
              </p>
              <p className="font-medium">{t('acceptOffer.offerProvider')}</p>
            </div>
          </div>
          <div className="space-y-2 border-t pt-3">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">
                {t('acceptOffer.offerPrice')}
              </span>
              <div className="flex items-center gap-1">
                <span>{offer.price.toLocaleString()}</span>
                <SaudiRiyal className="h-4 w-4" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">
                {t('acceptOffer.platformFee')}
              </span>
              <div className="flex items-center gap-1">
                <span>{(offer.price * 0.1).toLocaleString()}</span>
                <SaudiRiyal className="h-4 w-4" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">
                {t('acceptOffer.vat')}
              </span>
              <div className="flex items-center gap-1">
                <span>{(offer.price * 1.1 * 0.15).toLocaleString()}</span>
                <SaudiRiyal className="h-4 w-4" />
              </div>
            </div>
            <div className="flex items-center justify-between border-t pt-2">
              <span className="font-semibold">
                {t('acceptOffer.totalPrice')}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold">
                  {(offer.price * 1.1 * 1.15).toLocaleString()}
                </span>
                <SaudiRiyal className="h-5 w-5" />
              </div>
            </div>
          </div>
          {offer.notes && (
            <div className="border-t pt-3">
              <p className="text-muted-foreground mb-1 text-sm">
                {t('acceptOffer.vendorNotes')}
              </p>
              <p className="text-sm">{offer.notes}</p>
            </div>
          )}
          <div className="flex items-center justify-between border-t pt-3">
            <span className="text-muted-foreground">
              {t('offers.submissionDate')}
            </span>
            <span className="font-medium">{formatDate(offer.createdAt)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RequiredDocumentsCard({
  documents,
  onDocumentsChange,
  bankGuaranteePercent,
}: {
  documents: string[];
  onDocumentsChange: (documents: string[]) => void;
  bankGuaranteePercent: number | null;
}) {
  const { t } = useTranslation();
  const [noDocumentsRequired, setNoDocumentsRequired] = useState(false);
  const [newDocLabel, setNewDocLabel] = useState('');

  const handleAddDocument = () => {
    const trimmed = newDocLabel.trim();
    if (trimmed && !documents.includes(trimmed)) {
      onDocumentsChange([...documents, trimmed]);
      setNewDocLabel('');
    }
  };

  const handleRemoveDocument = (index: number) => {
    onDocumentsChange(documents.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddDocument();
    }
  };

  return (
    <Card className="mb-6">
      <CardContent className="p-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold">
            {t('acceptOffer.requiredDocuments')}
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">
            {t('acceptOffer.requiredDocumentsDescription')}
          </p>
        </div>

        <label className="flex cursor-pointer items-center gap-2">
          <Checkbox
            checked={noDocumentsRequired}
            onCheckedChange={(checked) => {
              setNoDocumentsRequired(checked === true);
              if (checked) {
                onDocumentsChange([]);
                setNewDocLabel('');
              }
            }}
          />
          <span className="text-sm">{t('acceptOffer.noDocumentsNeeded')}</span>
        </label>

        {!noDocumentsRequired && (
          <>
            <div className="mt-4 space-y-2">
              {documents.map((doc, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 rounded-lg border p-3"
                >
                  <span className="flex-1">{doc}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive/80 h-8 w-8 shrink-0"
                    onClick={() => handleRemoveDocument(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="mt-4 flex gap-2">
              <Input
                placeholder={t('acceptOffer.documentNamePlaceholder')}
                value={newDocLabel}
                onChange={(e) => setNewDocLabel(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleAddDocument}
                disabled={!newDocLabel.trim()}
              >
                <Plus className="h-4 w-4" />
                {t('common.add')}
              </Button>
            </div>
          </>
        )}

        {bankGuaranteePercent && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <Info className="h-5 w-5 shrink-0 text-amber-600" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                {t('acceptOffer.bankGuaranteeRequired')} ({bankGuaranteePercent}
                %)
              </p>
              <p className="text-sm text-amber-700">
                {t('acceptOffer.bankGuaranteeAutoAdded')}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AcceptOfferSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-32" />
      <Skeleton className="h-10 w-48" />
      <div className="space-y-4 rounded-lg border p-6">
        <Skeleton className="h-6 w-24" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
      <div className="space-y-4 rounded-lg border p-6">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-10 w-full" />
      </div>
      <Skeleton className="h-12 w-full" />
    </div>
  );
}
